import React, { useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { visuallyHidden } from "@mui/utils";
import PropTypes from "prop-types";

import {
  Box,
  IconButton,
  Stack,
  Switch,
  TablePagination,
  TableSortLabel,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { UilPlus, UilPen } from "@iconscout/react-unicons";
import Color from "../../enum/Color";
import { isNull } from "../../utils/Utils";
import { useContext } from "react";
import AuthContext from "../../store/auth-context";
import { baseUrl } from "../../Config";
import CreateButton from "../Common/CreateButton";
import TaskModal from "../Modal/TaskModal";
import { SendPutRequest } from "../../actions/Actions";
import BackdropContext from "../../store/backdrop-context";

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells = [
  {
    id: "name",
    numeric: false,
    disablePadding: false,
    label: "Name",
  },
  {
    id: "projectName",
    numeric: false,
    disablePadding: false,
    label: "Project",
  },

  {
    id: "active",
    numeric: false,
    disablePadding: false,
    label: "Status",
  },
  {
    id: "actions",
    numeric: false,
    disablePadding: false,
    label: "",
  },
];

function EnhancedTableHead(props) {
  const { order, orderBy, rowCount, onRequestSort } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={"left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

const EnhancedTableToolbar = () => {
  return (
    <Toolbar
      sx={{
        pl: { sm: 2 },
        pr: { xs: 1, sm: 1 },
      }}
    >
      <Typography
        sx={{ flex: "1 1 100%" }}
        variant="h6"
        id="tableTitle"
        component="div"
      >
        Tasks
      </Typography>
    </Toolbar>
  );
};

const TasksTable = (props) => {
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState(null);

  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("active");
  const [pagingInfo, setPagingInfo] = useState(props.pagingInfo);
  const [dense, setDense] = useState(false);

  const engagements = props.engagements;
  const tasks = props.tasks;
  const total = props.total;
  const employee = props.employee;

  const authContext = useContext(AuthContext);
  const backdropContext = useContext(BackdropContext);

  const closeModalHandler = () => {
    setShowModal((prev) => {
      return !prev;
    });

    props.refreshHandler();
  };

  const showDetails = (rowData, isReadOnly) => {
    setShowModal((prev) => {
      return !prev;
    });
    setModalData(() => {
      return rowData;
    });
  };

  const newModal = () => {
    setShowModal((prev) => {
      return !prev;
    });
    setModalData(() => {
      return null;
    });
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    const pi = {
      page: newPage,
      size: pagingInfo.size
    };
    setPagingInfo((prevState) => {
      return pi;
    },
      props.setPagingInfoCallback(pi)
    );
  };

  const handleChangeRowsPerPage = (event) => {
    const pi = {
      page: 0,
      size: parseInt(event.target.value, 10)
    }
    setPagingInfo((prevState) => {
      return pi;
    },
      props.setPagingInfoCallback(pi)
    );

  };

  const onSwitchChange = (row) => {
    backdropContext.toggle();
    const task = {
      id: row.id,
      userId: row.userId,
      name: row.name,
      project: row.project,
      active: !row.active,
    };
    updateHandler(task);
  };

  const updateHandler = async (task) => {
    const url = baseUrl + "/api/task";
    SendPutRequest(url, task, authContext.token, refreshHandler);
  };

  const refreshHandler = () => {
    
    backdropContext.toggle();
    const pi = {
      page: 0,
      size: pagingInfo.size
    };
    setPagingInfo((prevState) => {
      return pi;
    },
      props.setPagingInfoCallback(pi)
    );
  };

  const emptyRows =
    pagingInfo.page > 0 ? Math.max(0, (1 + pagingInfo.page) * pagingInfo.size - total) : 0;

  return (
    <div className="Table">
      <TaskModal
        isVisible={showModal}
        closeModalHandler={closeModalHandler}
        modalData={modalData}
        engagements={engagements}
        employee={employee}
      />
      <CreateButton action={newModal} icon={<UilPlus />} text="New Task" />
      <Box sx={{ width: "100%" }}>
        <Paper sx={{ width: "100%", mb: 2 }} elevation={5}>
          <EnhancedTableToolbar />
          <TableContainer>
            <Table
              sx={{ minWidth: 750 }}
              aria-labelledby="tableTitle"
              size={dense ? "small" : "medium"}
            >
              <EnhancedTableHead
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
                rowCount={total}
              />
              <TableBody>
                {/* if you don't need to support IE11, you can replace the `stableSort` call with:
                 rows.slice().sort(getComparator(order, orderBy)) */}
                {stableSort(tasks, getComparator(order, orderBy))
                  .map((row, index) => {
                    const labelId = `enhanced-table-checkbox-${index}`;
                    return (
                      <TableRow hover tabIndex={-1} key={row.id}>
                        <TableCell align="left">{row.name}</TableCell>
                        <TableCell align="left">
                          {!isNull(row) && row.projectName}
                        </TableCell>
                        <TableCell align="left">
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <Typography>Closed</Typography>
                            <Switch
                              color="success"
                              checked={row.active}
                              onChange={() => { onSwitchChange(row) }}
                            />
                            <Typography>Open</Typography>
                          </Stack>
                        </TableCell>
                        <TableCell align={"left"} className="Details">
                          <Tooltip title="Edit worktime">
                            <IconButton onClick={() => showDetails(row, false)}>
                              <UilPen style={{ color: Color.BACKGROUND }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                {emptyRows > 0 && (
                  <TableRow
                    style={{
                      height: (dense ? 33 : 53) * emptyRows,
                    }}
                  >
                    <TableCell colSpan={6} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={total}
            rowsPerPage={pagingInfo.size}
            page={pagingInfo.page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      </Box>
    </div>
  );
};

export default TasksTable;
